<template>
  <container
    :isModal="params.isSearch"
    @close-modal="cancelSelection()"
    :loading="!stateReady"
  >
    <template v-slot:header>
      <h3>Tabela de Preço</h3>
    </template>
    <template v-slot:body>
      <div>
        <form @submit.prevent="submitSearch">
          <entity-selector-cliente
            v-if="amIAllowed('view', 'root-cliente')"
            :cliente="cliente"
            :canEdit="!cliente"
            :idClienteFiltered="filter.idCliente"
            @confirm-selection="confirmSelectionCliente"
            :parentLoading="stateLoading || showEdit"
          />
          <fieldset :disabled="stateLoading || showEdit">
            <div class="p-formgrid p-grid">
              <div class="p-field p-fluid p-col-12 p-md-6">
                <entity-selector-orgao-aet
                  :idOrgao="filter.idOrgao ?? undefined"
                  @after-set-orgao="afterSetOrgao"
                />
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6">
                <input-with-label label="Tipo de Solicitação" v-slot="l">
                  <Dropdown
                    v-model="filter.tipoSolicitacao"
                    :options="tiposSolicitacao"
                    optionLabel="descricao"
                    optionValue="idTipoLicencaOrgao"
                    :showClear="true"
                    :id="l.id"
                    :placeholder="'&nbsp;'"
                  />
                </input-with-label>
              </div>
              <div class="p-col-12">
                <button-group>
                  <Button
                    type="submit"
                    class="p-button-rounded"
                    label="Buscar"
                  />
                  <Button
                    label="Limpar"
                    class="p-button-rounded p-button-text"
                    @click="clearFilter()"
                  />
                </button-group>
              </div>
            </div>
          </fieldset>
        </form>
      </div>
      <search-result
        :showSelector="params.isSearch"
        :results="results"
        :selected="params.selected"
        :multiple="params.isMultiple"
        :keyParam="'idTabelaPreco'"
        :loading="stateLoading"
        :showClient="true"
        @after-page-change="afterPageChange"
        @confirm-selection="confirmSelection"
      >
        <template v-slot:columns>
          <Column field="idOrgao" header="Órgão" style="width: 12rem">
            <template #body="slotProps">
              <span v-text="getNomeOrgao(slotProps.data.idOrgao)" />
            </template>
          </Column>
          <Column field="tipoSolicitacao" header="Tipo Solicitação">
            <template #body="slotProps">
              <span
                v-text="
                  getDescricaoTipoSolicitacao(slotProps.data.tipoSolicitacao)
                "
              />
            </template>
          </Column>
          <Column field="tipoVeiculo" header="Tipo Veículo" />
          <Column field="preco" header="Preço" style="width: 10rem">
            <template #body="slotProps">
              <span v-text="formatMoney(slotProps.data.preco)" />
            </template>
          </Column>
          <Column
            field="flAtivo"
            header="Ativo"
            style="width: 6rem; text-align: center"
          >
            <template #body="slotProps">
              <i
                v-if="slotProps.data.flAtivo"
                class="pi pi-check"
                style="color: #4CAF50"
              />
              <i v-else class="pi pi-times" style="color: #F44336" />
            </template>
          </Column>
          <Column :exportable="false" style="width: 10rem; text-align: right">
            <template #body="slotProps">
              <button-group>
                <Button
                  icon="pi pi-pencil"
                  class="p-button-rounded p-button-success"
                  :disabled="amIBlocked('view', slotProps.data.permissionInfo)"
                  @click="openEntity(slotProps.data.idTabelaPreco)"
                  @click.middle.prevent.stop="
                    openEntity(slotProps.data.idTabelaPreco, true)
                  "
                />
                <Button
                  icon="fas fa-trash-alt"
                  class="p-button-rounded p-button-danger"
                  v-if="
                    !params.isSearch &&
                      amIAllowed('shelve', slotProps.data.permissionInfo)
                  "
                  @click="shelveEntity(slotProps.data)"
                  :disabled="
                    amIBlocked('shelve', slotProps.data.permissionInfo)
                  "
                />
              </button-group>
            </template>
          </Column>
        </template>
        <template v-slot:actions>
          <Button
            label="Novo"
            :class="
              'p-button-rounded' + (params.isSearch ? ' p-button-text' : '')
            "
            v-if="amIAllowed('create', 'tabela-preco')"
            @click="newEntity()"
          />
        </template>
      </search-result>
      <div class="register" v-if="showEdit">
        <cadastro-tabela-preco
          :idTabelaPreco="idTabelaPreco"
          @after-register-screen-closed="afterRegisterScreenClosed"
          @after-save-new-entity="afterSaveNewEntity"
          :key="keyRegister"
          :parentLoading="stateLoading"
          :cliente="cliente"
        />
      </div>
    </template>
  </container>
</template>

<script lang="ts">
import CadastroTabelaPreco from "./CadastroTabelaPreco.vue";
import { PaginationResult, defaultResults } from "../../pagination";
import { useAuth, PermissionPayload } from "../../modules/auth";
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  PropType,
  Ref,
  watch
} from "vue";
import { RouteLocationNormalizedLoaded, useRouter } from "vue-router";
import {
  ClientePayload,
  useClienteService,
  useClienteLogadoService
} from "../../modules/business/cliente/clienteBusiness";
import {
  TabelaPrecoPayload,
  TabelaPrecoFilterPayload,
  useTabelaPrecoService
} from "../../modules/business/orcamento/tabelaPrecoBusiness";
import { useOrgaoAetService } from "../../modules/business/common/commonBusiness";
import { useTipoLicencaOrgaoService } from "../../modules/business/licenca/tipoLicencaOrgaoBusiness";
import { copyObject, clearObject } from "../../modules/utils";
import EntitySelectorCliente from "../cliente/EntitySelectorCliente.vue";
import EntitySelectorOrgaoAet from "../common/EntitySelectorOrgaoAet.vue";
import { focusOnLastElement } from "../../modules/globalAppProperties";
import { useConfirm } from "../../modules/message";

interface TabelaPrecoComponentParams {
  isSearch: boolean;
  selected: Array<TabelaPrecoPayload>;
  isNew?: boolean;
  isMultiple?: boolean;
  idTabelaPreco?: number;
  preFilter?: TabelaPrecoFilterPayload;
}

interface TabelaPrecoCrudState {
  idTabelaPreco: number | null;
  showEdit: boolean;
  results: PaginationResult<TabelaPrecoPayload>;
  filter: TabelaPrecoFilterPayload;
  page: number;
  stateReady: boolean;
  params: TabelaPrecoComponentParams;
  loadings: Array<Ref<boolean>>;
  stateId: string;
}

export default defineComponent({
  setup(props, { emit }) {
    const router = useRouter();

    const state = reactive<TabelaPrecoCrudState>({
      idTabelaPreco: null,
      showEdit: false,
      stateReady: false,
      filter: useTabelaPrecoService().safeFilterTabelaPreco(),
      page: 0,
      results: defaultResults(),
      params: {
        isSearch: props.isSearch,
        isNew: props.isNew,
        idTabelaPreco: props.idTabelaPreco,
        selected: props.selected ?? [],
        isMultiple: props.isMultiple,
        preFilter: props.preFilter
      },
      loadings: [],
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2)
    });

    copyObject(clearObject(props.preFilter), state.filter);

    const stateLoading = computed(() => state.loadings.length !== 0);

    const tiposSolicitacao = computed(() =>
      useTipoLicencaOrgaoService().getTiposLicencaOrgao()
    );

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => useAuth().amIAllowed(authority, permissionInfo);

    const amIBlocked = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => useAuth().amIBlocked(authority, permissionInfo);

    const keyRegister = computed(() => {
      return state.showEdit
        ? state.idTabelaPreco
          ? state.idTabelaPreco
          : "new-" +
            Math.random()
              .toString(36)
              .substring(2)
        : null;
    });

    const executeSearch = () => {
      const { data, loading, searchTabelasPreco } = useTabelaPrecoService();
      state.loadings.push(loading);
      return searchTabelasPreco(state.filter, state.page)
        .then(() => {
          state.results = data.value;
          state.page = state.results.page.number;
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const submitSearch = () => {
      state.page = 0;
      state.results = defaultResults();
      return executeSearch();
    };

    const getParamsRouteEdit = (idTabelaPreco: number) => {
      return {
        name: "tabela-preco-edit",
        params: { idTabelaPreco: idTabelaPreco + "" }
      };
    };

    const processViewRoute = (
      route: Partial<RouteLocationNormalizedLoaded>
    ) => {
      if (route.name == "tabela-preco-new") {
        state.showEdit = true;
        state.idTabelaPreco = null;
      } else if (route.name == "tabela-preco-edit") {
        if (route.params && route.params.idTabelaPreco) {
          state.idTabelaPreco = Array.isArray(route.params.idTabelaPreco)
            ? +route.params.idTabelaPreco[0]
            : +route.params.idTabelaPreco;
        } else {
          state.idTabelaPreco = null;
        }
        state.showEdit = true;
      } else {
        state.showEdit = false;
        state.idTabelaPreco = null;
        executeSearch();
      }
    };

    const confirmSelection = (selected: Array<TabelaPrecoPayload>) => {
      emit("confirm-selection", selected);
    };

    const cancelSelection = () => {
      emit("cancel-selection");
    };

    const shelveEntity = (entity: TabelaPrecoPayload) => {
      useConfirm().require({
        message: "Deseja arquivar esta tabela de preço?",
        header: "Atenção",
        icon: "pi pi-info-circle",
        acceptClass: "p-button-danger",
        accept: () => {
          if (entity && entity.idTabelaPreco) {
            const { loading, shelveTabelaPreco } = useTabelaPrecoService();
            state.loadings.push(loading);
            shelveTabelaPreco(entity.idTabelaPreco)
              .then(() => {
                executeSearch();
                state.loadings.splice(state.loadings.indexOf(loading), 1);
              })
              .catch(() => {
                state.loadings.splice(state.loadings.indexOf(loading), 1);
              });
          }
        }
      });
    };

    const openEntity = (idTabelaPreco: number, newTab?: boolean) => {
      const routeParams = getParamsRouteEdit(idTabelaPreco);
      if (newTab) {
        window.open(router.resolve(routeParams).fullPath);
      } else if (state.params.isSearch) {
        processViewRoute(routeParams);
      } else {
        router.push(routeParams);
      }
    };

    const newEntity = () => {
      const routeParams = { name: "tabela-preco-new" };
      if (state.params.isSearch) {
        processViewRoute(routeParams);
      } else {
        router.push(routeParams);
      }
    };

    const afterRegisterScreenClosed = () => {
      const routeParams = { name: "tabela-preco" };
      if (state.params.isSearch) {
        processViewRoute(routeParams);
      } else {
        router.push(routeParams);
      }
    };

    const afterSaveNewEntity = (idTabelaPreco: number) => {
      openEntity(idTabelaPreco);
    };

    const afterPageChange = (page: number) => {
      state.page = page;
      executeSearch();
    };

    const clearFilter = () => {
      state.filter = useTabelaPrecoService().safeFilterTabelaPreco();
      state.results = defaultResults();
    };

    const confirmSelectionCliente = (c?: ClientePayload) => {
      state.filter.idCliente = c && c.idCliente ? c.idCliente : undefined;
    };

    const afterSetOrgao = (idOrgao: number | null) => {
      state.filter.idOrgao = idOrgao;
    };

    const getNomeOrgao = (idOrgao?: number | null) => {
      return useOrgaoAetService().getOrgaoAetStr(idOrgao ?? undefined);
    };

    const getDescricaoTipoSolicitacao = (
      idTipoLicencaOrgao?: number | null
    ) => {
      if (idTipoLicencaOrgao === null || idTipoLicencaOrgao === undefined)
        return "";
      return (
        useTipoLicencaOrgaoService().getTipoLicencaOrgao(idTipoLicencaOrgao)
          ?.descricao ?? ""
      );
    };

    const formatMoney = (valor?: number | null) => {
      if (valor === null || valor === undefined) return "";
      return valor.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL"
      });
    };

    const getNomeCliente = (e?: ClientePayload) => {
      return (amIAllowed("view", "root-cliente")
        ? useClienteService()
        : useClienteLogadoService()
      ).getNomeCliente(e);
    };

    const deveAbrirTelaCadastro =
      state.params.idTabelaPreco || state.params.isNew;
    if (deveAbrirTelaCadastro) {
      if (state.params.idTabelaPreco) {
        state.idTabelaPreco = state.params.idTabelaPreco;
      }
      state.showEdit = true;
    }

    onMounted(async () => {
      if (props.cliente && props.cliente.idCliente) {
        state.filter.idCliente = props.cliente.idCliente;
      }
      state.stateReady = true;
      if (!state.params.isSearch) {
        watch([router.currentRoute], () => {
          processViewRoute(router.currentRoute.value);
        });
      }
      if (!deveAbrirTelaCadastro) {
        submitSearch().then(() => focusOnLastElement());
      }
    });

    return {
      submitSearch,
      executeSearch,
      openEntity,
      newEntity,
      shelveEntity,
      confirmSelection,
      cancelSelection,
      afterRegisterScreenClosed,
      afterSaveNewEntity,
      afterPageChange,
      clearFilter,
      keyRegister,
      amIAllowed,
      amIBlocked,
      getNomeCliente,
      getNomeOrgao,
      getDescricaoTipoSolicitacao,
      formatMoney,
      tiposSolicitacao,
      stateLoading,
      confirmSelectionCliente,
      afterSetOrgao,
      ...toRefs(state)
    };
  },
  components: {
    CadastroTabelaPreco,
    EntitySelectorCliente,
    EntitySelectorOrgaoAet
  },
  props: {
    isSearch: Boolean,
    isNew: Boolean,
    isMultiple: Boolean,
    idTabelaPreco: Number,
    preFilter: Object as PropType<TabelaPrecoFilterPayload>,
    cliente: Object as PropType<ClientePayload>,
    selected: {
      type: Array as PropType<Array<TabelaPrecoPayload>>
    }
  }
});
</script>

<style scoped lang="scss"></style>
